/**
 * Override New Relic actionText for elements with data-nr-action attributes
 * to prevent PII from being captured in user action tracking.
 *
 * This addresses the issue where New Relic browser agent v1.299.0+ captures
 * text content of interactive elements (like anchor tags with click handlers)
 * as part of user frustration tracking.
 *
 * Usage: Add data-nr-action="safe.label.name" to elements you want to track
 * with a safe, non-PII label instead of the element's text content.
 */

import loggingService from "services/loggingService";

declare global {
    interface Window {
        newrelic?: {
            interaction?: () =>
                | {
                      actionText?: (text: string) => void;
                  }
                | undefined;
        };
    }
}

let isInitialized = false;
let hasLoggedFailure = false;

/**
 * Click handler function that overrides New Relic actionText for elements with data-nr-action.
 */
function handleClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (!target) {
        return;
    }

    // Find the closest element with data-nr-action attribute
    const elementWithAction = target.closest("[data-nr-action]");
    if (!elementWithAction) {
        return;
    }

    // Get the safe action name from the attribute
    const safeName = elementWithAction.getAttribute("data-nr-action");
    if (!safeName) {
        return;
    }

    // Override actionText with the safe label
    try {
        window.newrelic?.interaction?.()?.actionText?.(safeName);
    } catch (error) {
        if (!hasLoggedFailure) {
            loggingService.warn(
                "Failed to set New Relic actionText:",
                safeName
            );
            hasLoggedFailure = true;
        }
    }
}

/**
 * Initialize New Relic actionText override for elements with data-nr-action attributes.
 * Adds the event listener to the document.
 * This should be called after the DOM is ready.
 * since handleClick uses safe navigation to handle cases where New Relic loads asynchronously.
 */
export function initNewRelicActionTextOverride(): void {
    // Prevent duplicate initialization
    if (typeof window === "undefined" || isInitialized) {
        return;
    }

    // Add click event listener in capture phase to run early
    document.addEventListener("click", handleClick, true);

    isInitialized = true;
}

/**
 * Cleanup function that removes the New Relic actionText override event listener.
 * Should be called when the app is disposed.
 */
export function cleanupNewRelicActionTextOverride(): void {
    if (!isInitialized) {
        return;
    }

    // Remove the event listener using the same handler reference
    document.removeEventListener("click", handleClick, true);

    // Reset state
    isInitialized = false;
    hasLoggedFailure = false;
}
